import * as React from 'react';

/**
 * Per-ticket composer drafts, persisted in localStorage.
 *
 * Support work is interrupt-driven: an admin starts a reply, gets pulled into
 * another ticket, and comes back. Losing a half-written reply is one of the
 * worst small papercuts in a queue-based tool, so the draft is keyed per ticket
 * and survives ticket switches, panel closes and page reloads.
 *
 * Drafts stay in the browser — nothing is written to the server until the admin
 * actually sends. They expire after DRAFT_MAX_AGE_MS so stale text can't
 * resurface months later, and empty text removes the entry.
 */

const KEY_PREFIX = 'isats:ticket-draft:';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const SAVE_DEBOUNCE_MS = 400;

export interface TicketDraft {
  body: string;
  isInternal: boolean;
  savedAt: string;
}

function storageKey(ticketId: string): string {
  return `${KEY_PREFIX}${ticketId}`;
}

export function readTicketDraft(ticketId?: string | null): TicketDraft | null {
  if (!ticketId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(ticketId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TicketDraft;
    if (!parsed || typeof parsed.body !== 'string' || !parsed.body.trim()) return null;
    if (parsed.savedAt && Date.now() - Date.parse(parsed.savedAt) > DRAFT_MAX_AGE_MS) {
      window.localStorage.removeItem(storageKey(ticketId));
      return null;
    }
    return { body: parsed.body, isInternal: !!parsed.isInternal, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function clearTicketDraft(ticketId?: string | null): void {
  if (!ticketId || typeof window === 'undefined') return;
  try { window.localStorage.removeItem(storageKey(ticketId)); } catch { /* ignore */ }
}

/**
 * Draft state for one ticket. `restoredDraft` is the draft found when the ticket
 * changed (null when there isn't one); `saveDraft` is debounced and safe to call
 * on every keystroke; `discardDraft` deletes it.
 */
export function useTicketDraft(ticketId?: string | null) {
  const [restoredDraft, setRestoredDraft] = React.useState<TicketDraft | null>(null);
  const loadedForRef = React.useRef<string | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = React.useRef<{ ticketId: string; body: string; isInternal: boolean } | null>(null);
  const ticketIdRef = React.useRef<string | null>(ticketId ?? null);
  ticketIdRef.current = ticketId ?? null;

  // Re-read only when the ticket changes, so typing never re-triggers a restore.
  React.useEffect(() => {
    if (!ticketId || loadedForRef.current === ticketId) return;
    loadedForRef.current = ticketId;
    setRestoredDraft(readTicketDraft(ticketId));
  }, [ticketId]);

  const flush = React.useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const pending = pendingRef.current;
    pendingRef.current = null;
    // Use the ticket the keystrokes belong to — NOT ticketIdRef.current, which
    // already points at the new ticket by the time a switch cleanup runs.
    const id = pending ? pending.ticketId : ticketIdRef.current;
    if (!id || !pending) return;
    try {
      if (!pending.body.trim()) window.localStorage.removeItem(storageKey(id));
      else window.localStorage.setItem(storageKey(id), JSON.stringify({
        body: pending.body, isInternal: pending.isInternal, savedAt: new Date().toISOString(),
      } satisfies TicketDraft));
    } catch { /* quota / private mode — drafts are best-effort */ }
  }, []);

  const saveDraft = React.useCallback((body: string, isInternal: boolean) => {
    const id = ticketIdRef.current;
    if (!id) return;
    pendingRef.current = { ticketId: id, body, isInternal };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }, [flush]);

  const discardDraft = React.useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    pendingRef.current = null;
    clearTicketDraft(ticketIdRef.current);
    setRestoredDraft(null);
  }, []);

  const clearDraft = React.useCallback(() => { discardDraft(); }, [discardDraft]);

  // Never lose the last keystrokes on unmount / ticket switch.
  React.useEffect(() => () => flush(), [flush, ticketId]);

  return { restoredDraft, saveDraft, clearDraft, discardDraft };
}
